// DrawerCreateTaskForm — slim「新增任務」form for the AI 導覽 drawer.
// Drives the same /v1/tasks POST as the Tasks page's TaskForm but
// keeps only the「快速建立」 fields: name + 一句話摘要. Verification
// toggles + TipTap SOP doc + assignment editing live on the full
// /dashboard/tasks page; the drawer's job is "get a task row created
// fast so AI can chain follow-ups" (e.g. "要不要把這任務派發到某巡邏
// 點上？" — that's a separate plan or assignment form).
//
// AM-7c spec docs/specs/ai-mode-operations-console.md §5.2.

import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { createTask, friendlyTasksError } from "../lib/tasksApi.js";

interface Props {
  prefill: Record<string, unknown>;
  tenantId: string | null;
  onSaved: (entityId: string) => void;
  onCancel: () => void;
}

function readStringPrefill(prefill: Record<string, unknown>, key: string): string {
  const v = prefill[key];
  return typeof v === "string" ? v : "";
}

export default function DrawerCreateTaskForm({
  prefill, tenantId, onSaved, onCancel,
}: Props): React.ReactElement {
  const [name, setName] = useState(readStringPrefill(prefill, "name"));
  const [descShort, setDescShort] = useState(readStringPrefill(prefill, "description_short"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await createTask({
        name: name.trim(),
        description_short: descShort.trim() || null,
        // Defaults: no verifications, allow skip, active. Operator
        // edits these on /dashboard/tasks if needed. Drawer scope is
        // intentionally minimal — "get a task row in fast".
        requires_text: false,
        requires_camera: false,
        requires_file: false,
        verify_device: false,
        verify_face: false,
        verify_geo: false,
        requires_signature: false,
        allow_skip: true,
        status: "active",
      }, tenantId);
      onSaved(r.id);
    } catch (e) {
      setErr(friendlyTasksError(e));
    } finally {
      setBusy(false);
    }
  };

  const canSave = name.trim().length > 0 && !busy;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">任務名稱 *</label>
        <input
          className="input w-full"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="例：機房深夜巡查"
          maxLength={200}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">一句話摘要</label>
        <input
          className="input w-full"
          value={descShort}
          onChange={(e) => setDescShort(e.target.value)}
          placeholder="例：每日 22:00–06:00 機房門禁 + 溫度確認"
          maxLength={500}
        />
        <p className="mt-1 text-2xs text-text-muted">顯示在列表的次標題</p>
      </div>
      <div className="rounded border border-border-subtle bg-bg-raised p-3 text-2xs text-text-muted leading-relaxed">
        <p className="mb-1 font-medium text-text-secondary inline-flex items-center gap-1">
          <ExternalLink className="h-3 w-3" /> 進階設定到「任務」頁編輯
        </p>
        <p>圖文 SOP 文件、簽名 / 拍照 / 人臉 / 地點驗證等選項，請建立後到「任務」頁編輯。</p>
        <p className="mt-1">建立完後可請 AI 派發此任務到指定地點。</p>
      </div>
      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {err}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary text-sm">取消</button>
        <button type="button" onClick={() => void save()} disabled={!canSave} className="btn-primary text-sm">
          {busy ? "儲存中…" : "新增"}
        </button>
      </div>
    </div>
  );
}
